import React, { useEffect, useRef } from "react";
import styled from "styled-components";

const CrossOverText = styled.canvas`
    display: block;
    width: 100%;
    pointer-events: none;
    font-size: 4rem;
    font-family: var(--ff-title);

    body.theme-platinum & {
        font-size: 2rem;
    }

    body.theme-market & {
        color: var(--text-primary);
        font-size: 2em;
    }
`;

const NotAReceiptOverlay = ({ onRender }: { onRender?: (image: string) => void }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        let cancelled = false;
        const draw = () => {
            if (cancelled) {
                return;
            }

            const width = canvas.getBoundingClientRect().width;
            const context = canvas.getContext('2d');
            if (!context || !width) {
                return;
            }

            const styles = window.getComputedStyle(canvas);
            const text = 'NO VÁLIDO COMO BOLETO';
            const angle = -10 * Math.PI / 180;
            const padding = 8;
            const topPadding = 200;
            let fontSize = parseFloat(styles.fontSize);
            context.font = `${fontSize}px ${styles.fontFamily}`;
            const textWidth = context.measureText(text).width;
            const rotatedWidth = textWidth * Math.cos(angle) + fontSize * 1.4 * Math.abs(Math.sin(angle));
            const maxWidth = Math.min(rotatedWidth + padding * 2, onRender ? 500 : Infinity);
            const drawingWidth = Math.min(width, maxWidth);
            const scale = Math.min(1, Math.max(1, drawingWidth - padding * 2) / rotatedWidth);
            fontSize *= scale;
            const height = Math.ceil(textWidth * scale * Math.abs(Math.sin(angle)) + fontSize * 1.4 * Math.cos(angle)) + padding * 2 + topPadding;
            const pixelRatio = window.devicePixelRatio || 1;

            canvas.width = Math.round(drawingWidth * pixelRatio);
            canvas.height = Math.round(height * pixelRatio);
            canvas.style.maxWidth = `${maxWidth}px`;
            canvas.style.height = `${height}px`;
            context.scale(pixelRatio, pixelRatio);
            context.translate(drawingWidth / 2, topPadding + (height - topPadding) / 2);
            context.rotate(angle);
            context.font = `${fontSize}px ${styles.fontFamily}`;
            context.fillStyle = styles.color;
            context.globalAlpha = 0.1;
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(text, 0, 0);

            if (onRender) {
                onRender(`url(${canvas.toDataURL()})`);
            }
        };

        draw();
        window.addEventListener('resize', draw);
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(draw) : null;
        if (observer && canvas.parentElement) {
            observer.observe(canvas.parentElement);
        }
        if (document.fonts) {
            document.fonts.ready.then(draw);
        }

        return () => {
            cancelled = true;
            window.removeEventListener('resize', draw);
            if (observer) {
                observer.disconnect();
            }
        };
    }, [onRender]);

    return (
        <CrossOverText innerRef={canvasRef} role="img" aria-label="No válido como boleto" />
    );
}

export default NotAReceiptOverlay;